
import { useState, useEffect } from 'react';
import { MortgageRate } from '../types';
import { baseRates as staticRates, RATES_LAST_UPDATED as staticDate } from '../data/rates';
import { getFallbackHistoricalRates } from '../data/historicalRates';

export const useMortgageRates = () => {
  // Initial state combines static rates with dynamic fallback history
  const [rates, setRates] = useState<MortgageRate[]>(() => {
      const fallbackHistory = getFallbackHistoricalRates();
      return staticRates.map(r => ({
          ...r,
          history: (fallbackHistory as any)[r.type]
      }));
  });
  
  const [lastUpdated, setLastUpdated] = useState<string>(staticDate);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  useEffect(() => {
    let isMounted = true;

    const fetchRates = async () => {
      try {
        setIsLoading(true);
        const response = await fetch('/api/rates');
        
        if (!response.ok) {
            throw new Error(`API Error: ${response.statusText}`);
        }
        
        const data = await response.json();
        
        if (isMounted && data.rates && Array.isArray(data.rates)) {
          setRates(data.rates); // This now includes history from the API
          setLastUpdated(data.updatedAt || new Date().toLocaleDateString());
          setIsError(false);
        }
      } catch (error) {
        console.warn("Could not fetch dynamic rates, using static fallback.", error);
        setIsError(true);
        // Fallback logic is handled in initial state, but if we wanted to refresh it:
        const fallbackHistory = getFallbackHistoricalRates();
        setRates(staticRates.map(r => ({ ...r, history: (fallbackHistory as any)[r.type] })));
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchRates();

    return () => {
      isMounted = false;
    };
  }, []);

  return { rates, lastUpdated, isLoading, isError };
};
